<template>
  <div
    v-if="allDirectRetailers && allDirectRetailers.length"
    v-click-outside="closeDropdown"
    class="v2-direct-retailer-dropdown"
  >
    <v2-button
      v-if="selectedRetailer"
      variant="outline"
      @click="onExitRetailerView"
    >
      Exit Retailer View
    </v2-button>
    <div
      v-else
      class="v2-direct-retailer-dropdown__wrapper"
    >
      <v2-button
        variant="outline"
        @click="toggleDropdown"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle
            cx="12"
            cy="7"
            r="4"
          />
        </svg>
        View as Retailer
      </v2-button>
      <div
        v-if="isOpen"
        class="v2-direct-retailer-dropdown__menu"
      >
        <div class="v2-direct-retailer-dropdown__header">
          <h4 class="v2-heading-xs v2-m-0">
            My Direct Retailers
          </h4>
          <v2-badge variant="primary">
            {{ filteredRetailers.length }} retailer{{ filteredRetailers.length !== 1 ? 's' : '' }}
          </v2-badge>
        </div>
        <div class="v2-direct-retailer-dropdown__search">
          <svg
            class="v2-direct-retailer-dropdown__search-icon"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle
              cx="11"
              cy="11"
              r="8"
            />
            <line
              x1="21"
              y1="21"
              x2="16.65"
              y2="16.65"
            />
          </svg>
          <v2-input
            v-model="search"
            placeholder="Search Retailer"
          />
        </div>
        <div
          v-if="filteredRetailers.length"
          class="v2-direct-retailer-dropdown__list"
        >
          <div
            v-for="retailer in filteredRetailers"
            :key="retailer.walletRuleId"
            class="v2-direct-retailer-dropdown__item"
            :class="{ 'v2-direct-retailer-dropdown__item--active': selectedRetailer && retailer.retailerId === selectedRetailer.retailerId }"
            @click="onSelectRetailer(retailer)"
          >
            <div class="v2-direct-retailer-dropdown__item-content">
              <h6 class="v2-direct-retailer-dropdown__item-title">
                {{ retailer.entityName }} {{ retailer.retailerBrandIdentity && `(${retailer.retailerBrandIdentity})` }}
              </h6>
              <p class="v2-direct-retailer-dropdown__item-subtitle">
                {{ retailer.email }}
              </p>
            </div>
          </div>
        </div>
        <div
          v-else
          class="v2-direct-retailer-dropdown__empty"
        >
          Retailers not found
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from '@vue/composition-api'
import { UPDATE_SELECTED_DIRECT_RETAILER } from '@/store/modules/shop.module'
import analytics from '@/@core/utils/analytics'
import constants from '@/constants'
import { debounce } from '@/@core/utils/utils'
import { FETCH_DIRECT_RETAILERS } from '@/store/modules/connections.module'
import V2Input from './V2Input.vue'
import V2Badge from './V2Badge.vue'
import V2Button from './V2Button.vue'

export default {
  name: 'V2DirectRetailerDropdown',
  components: {
    V2Button,
    V2Badge,
    V2Input,
  },
  directives: {
    clickOutside: {
      bind(el, binding) {
        el.clickOutsideEvent = event => {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value(event)
          }
        }
        document.addEventListener('click', el.clickOutsideEvent)
      },
      unbind(el) {
        document.removeEventListener('click', el.clickOutsideEvent)
      },
    },
  },
  setup(props, { root }) {
    const allDirectRetailers = ref([])
    const selectedRetailer = ref(null)
    const isOpen = ref(false)
    const search = ref('')

    const debouncedTrackSearch = debounce(searchTerm => {
      analytics.track(constants.TRACKS.ACTIONS.USER_SEARCH_RETAILERS, { searchTerm })
    })

    const filteredRetailers = computed(() => {
      const searchTerm = search.value?.trim().toLowerCase()
      return allDirectRetailers.value.filter(
        retailer => retailer.entityName?.toLowerCase().includes(searchTerm)
          || retailer.retailerBrandIdentity?.toLowerCase().includes(searchTerm)
          || retailer.email?.toLowerCase().includes(searchTerm),
      )
    })

    watch(search, newVal => {
      if (newVal) {
        debouncedTrackSearch(newVal)
      }
    })

    const getDirectRetailer = () => {
      root.$store.dispatch(FETCH_DIRECT_RETAILERS).then(res => {
        allDirectRetailers.value = res
      })
    }

    const toggleDropdown = () => {
      isOpen.value = !isOpen.value
      if (isOpen.value) {
        analytics.track(constants.TRACKS.ACTIONS.BRAND_CLICKS_ON_VIEW_AS_RETAILER)
      }
    }

    const closeDropdown = () => {
      isOpen.value = false
    }

    const onSelectRetailer = retailer => {
      selectedRetailer.value = retailer
      root.$root.$refs.shopItems?.setDirectRetailerInfo(retailer.retailerId)
      root.$store.commit(UPDATE_SELECTED_DIRECT_RETAILER, retailer)
      search.value = ''
      isOpen.value = false
      analytics.track(constants.TRACKS.ACTIONS.BRAND_SELECT_RETAILER, { retailerId: retailer.retailerId })
    }

    const onExitRetailerView = () => {
      const retailerId = selectedRetailer.value.retailerId
      selectedRetailer.value = null
      root.$root.$refs.shopItems?.setDirectRetailerInfo()
      root.$store.commit(UPDATE_SELECTED_DIRECT_RETAILER, null)
      analytics.track(constants.TRACKS.ACTIONS.BRAND_CLICKS_ON_EXIT_RETAILER_VIEW, {
        retailer: retailerId,
      })
    }

    getDirectRetailer()

    return {
      allDirectRetailers,
      selectedRetailer,
      isOpen,
      search,
      filteredRetailers,
      toggleDropdown,
      closeDropdown,
      onSelectRetailer,
      onExitRetailerView,
    }
  },
}
</script>

<style lang="scss" scoped>
.v2-direct-retailer-dropdown {
  &__wrapper {
    position: relative;
  }

  // Floating surface — near-opaque white so dark text stays readable when
  // the menu opens over arbitrary content (product images, dark cards).
  &__menu {
    position: absolute;
    top: calc(100% + 0.5rem);
    right: 0;
    z-index: 1000;
    min-width: 370px;
    background: var(--v2-glass-surface-floating);
    backdrop-filter: blur(var(--v2-glass-blur-sm)) saturate(160%);
    border: 1px solid var(--v2-glass-stroke);
    border-radius: 1rem;
    box-shadow: var(--v2-glass-shadow-floating);
    padding: var(--v2-space-3);
    max-height: 500px;
    display: flex;
    flex-direction: column;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--v2-space-3);
  }

  &__search {
    position: relative;
    margin-bottom: var(--v2-space-3);

    ::v-deep .v2-input {
      padding-left: var(--v2-space-8);
    }
  }

  &__search-icon {
    position: absolute;
    top: 50%;
    left: var(--v2-space-3);
    transform: translateY(-50%);
    pointer-events: none;
    z-index: 1;
    color: var(--v2-muted-foreground);
  }

  &__list {
    overflow-y: auto;
    max-height: 300px;
  }

  &__item {
    padding: var(--v2-space-2);
    cursor: pointer;
    border-radius: var(--v2-radius-md);
    transition: background 0.15s ease;

    &:hover {
      background: var(--v2-muted);
    }

    &--active {
      background: var(--v2-accent);
    }
  }

  &__item-content {
    display: flex;
    flex-direction: column;
  }

  &__item-title {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--v2-foreground);
    margin: 0;
  }

  &__item-subtitle {
    font-size: 0.75rem;
    color: var(--v2-muted-foreground);
    margin: 0;
  }

  &__empty {
    text-align: center;
    padding: var(--v2-space-4);
    color: var(--v2-muted-foreground);
    font-size: 0.875rem;
  }
}
</style>
